import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, OnInit, ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { Observable ,  Subject, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { publishedMarketingCopyToast } from 'shared/toaster/toasts';


import { BrandInformationService } from '../../brand-information.service';
import { InformationPageService } from '../../../information-page/information-page.service';
import { BrandService } from 'shared/brand/brand.service';
import { MarketingCopyService } from 'shared/marketing-copy/marketing-copy.service';
import { getAvailableLanguages } from 'utils/utils';
import { marketingCodeLookup } from 'utils/lookups';
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { MARKETING_NAME } from '../../variables';
import { MarketingFormComponent } from './marketing-form.component';
import { PermissionsService } from 'shared/permissions/permissions.service';
import {takeWhile} from 'rxjs/operators';
import {ProductPanelComponent} from '../../../../product-panel/product-panel.component';
import {BRAND_NAME} from '../../../account-information/variables';


@Component({
  moduleId: module.id,
  selector: 'marketing-segment',
  template: `
    <marketing-form
      [brandId]="brandId"
      [data]='data'
      [lookups]="lookups"
      [segmentData]='segmentData'
      [savingEvents]="marketingCopyService.isSavedObservable"
      (cancel)='cancelClickHandler()'
      (saveChanges)='saveChangesClickHandler()'
    ></marketing-form>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class MarketingComponent extends BaseSegmentComponent implements OnInit {
  name: string = MARKETING_NAME;
  brandId: string;
  omitFields: Array<string> = [
    'languages', 'data', 'languageCode',
    'selectedLanguage', 'value', 'codeLookup'
  ];
  lookups: any = {};

  @ViewChild(MarketingFormComponent, {static: true})
  // @ts-ignore
  protected formComponent: MarketingFormComponent;

  constructor(
    public marketingCopyService: MarketingCopyService,
    protected router: Router,
    protected brandInformationService: BrandInformationService,
    protected informationPageService: InformationPageService,
    protected permissionsService: PermissionsService,
    protected ref: ChangeDetectorRef,
    private brandService: BrandService,
    private toastrService: ToastrService,
  ) {
    super(
      router,
      brandInformationService,
      informationPageService,
      permissionsService,
      ref,
    );
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  // show action panel always
  get showActionPanel(): boolean {
    return true;
  }

  // redefined method
  get showPublishAction(): boolean {
    return !this.isDirty;
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.brandId = this.brandService.getBrand();
    this.refresh();
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  setData(data: any) {
    this.fetchAssignmentLookups()
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((lookups: Array<any>) => {
      this.data = this.reorganizeMarketingData(data);
      this.sourceData = [...this.data];
      _.forEach(this.data, item => {
        if (_.find(item.data, {language_code: 'EN'})) {
          item.selectedLanguage = 'EN';
        } else {
          item.selectedLanguage = item.data[0].language_code;
        }
      });
      this.ref.markForCheck();
      this.hideLoader();
    });
  }

  fetchFormData(): Observable<any> {
    return this.marketingCopyService.fetchMarketCopy(this.brandId);
  }

  putFormData(publish: boolean= false): Observable<any> {
    const data = this.prepareDataForSave(this.formComponent.marketingFormData.value);
    return this.marketingCopyService.putMarketCopy(this.brandId, data, publish);
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  /*
    event: {
      currentSegment: string, // segment name
      isSaveBeforeLeaving: boolean,
      updateItems: boolean,
    }
  */
  onSaved(event: any) {
    this.marketingCopyService.emitIsSaved();
    if (_.get(event, 'updateItems', false)) {
      this.toastrService.info('', '', publishedMarketingCopyToast());
    }
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  // group marketing data by by the name, reference, code, and type
  // sort by Reference Type(code prop) and Assignment(reference prop). Order is taken from lookups
  private reorganizeMarketingData(data: Array<any>): Array<any> {
    const fields = ['name', 'reference', 'code', 'type'];
    const groups: any = _.chain(data)
      .reduce((res: Array<any>, item: any) => {
        const common = _.pick(item, fields);
        // find element with the same fields
        const matchEl = _.find(res, common);
        if (matchEl) {
          // if this element exists - extend copies
          this.extendMarketingItem(matchEl, item);
        } else {
          // if not - create new
          const newItem = this.createMarketingItem(item);
          res.push(newItem);
        }
        return res;
      },  [])
      .groupBy('code')
      .value();
    // sort items inside groups by Assignment (reference prop)
    return _.chain(marketingCodeLookup)
      .map((code: any) => {
        return _.sortBy(groups[code.option], (item: any) => {
          return _.findIndex(this.lookups[code.option], ['option', item.reference]);
        });
      })
      .flatten()
      .value();
  }

  private extendMarketingItem(item: any, addItem: any) {
    // find item with the same language
    const matchLang: any = _.find(item.data, {language_code: addItem.language_code});
    if (matchLang) {
      // if this element exists - extend it
      this.addDataToMarketingSubitem(matchLang, addItem);
    } else {
      // if not - create new
      const marketingSubitem = this.createMarketingSubitem(addItem.language_code);
      this.addDataToMarketingSubitem(marketingSubitem, addItem);
      item.data.push(marketingSubitem);
    }
  }

  private createMarketingSubitem(language_code: string): any {
    return {
      language_code,
      copies: <any>[],
    };
  }

  private addDataToMarketingSubitem(marketingSubitem: any, item: any) {
    if (item?.value !== null && item?.value !== undefined) {
      //  copy:
      //  {
      //    value: String,
      //    origin_id: String,
      //  },
      marketingSubitem.copies.push({
        value: item.value,
        origin_id: item.origin_id ? item.origin_id : null,
      });
    }
  }

  private addLanguage(item: any, language_code: string): void {
    // add all available languages (for now it's english, french, spanish)
    if (item.languages) { return; }
    item.languages = getAvailableLanguages();
  }

  private createMarketingItem(item: any = {
      name: '',
      reference: '',
      type: '',
      code: '',
      sub_code : '',
      sub_code_reference: '',
      language_code: 'EN',
      selectedLanguage: 'EN',
      value: '',
      languages: [],
      data: [{
        copies: [],
        language_code: 'EN',
      }],
    }) {
      const newItem = Object.assign({}, item);
      const marketingSubitem = this.createMarketingSubitem(item.language_code);
      this.addDataToMarketingSubitem(marketingSubitem, item);
      // add data field - array with the grouping by languages:
      // {
      //  copies: [
      //  {
      //    value: String,
      //    origin_id: String,
      //  },
      //  language_code: String,
      // }
      newItem.data = [marketingSubitem];
      this.addLanguage(newItem, item.language_code);
      return newItem;
  }

  private prepareDataForSave(data: any): any {
    return _.reduce(data, (result: Array<any>, item: any) => {
      // omit excess fields
      const obj: any = _.omit(item, this.omitFields);
      // create object with data for every copy value
      _.each(item.data, (copiesData: any) => {
        obj.languageCode = copiesData.languageCode;
        // remove copies with empty value
        _.remove(copiesData.copies, (copy: any) => !copy.value);
        if (copiesData.copies.length) {
          _.each(copiesData.copies, (copy: any) => {
            const dataItem = this.createDataItem(obj, copy);
            result.push(dataItem);
          });
        } else {
          // create empty item with english language only
          if (copiesData.languageCode !== 'EN') { return; }
          const dataItem = this.createDataItem(obj, {
            value: '',
            originId: null,
          });
          result.push(dataItem);
        }
      });
      return result;
    }, []);
  }

  private createDataItem(obj: any, copy: any): any {
    const dataItem = _.clone(obj);
    dataItem.value = copy.value;
    dataItem.originId = copy.origin_id;
    dataItem.digitalAssets = obj.digitalAssets.map(asset => asset.asset);
    if (!dataItem.brandRelationshipId) {
      dataItem.brandRelationshipId = this.brandId;
    }
    return dataItem;
  }

  private createCopy(text: string, originId: string): any {
    return {
      value: text,
      originId,
    };
  }

  private fetchAssignmentLookups(): Observable<any> {
    if (_.size(this.lookups)) { return of(this.lookups); }
    const subj = new Subject<any>();
    _.each(marketingCodeLookup, (item: any, index: number) => {
      this.marketingCopyService.fetchLookup(this.brandId, item.option)
      .subscribe((data: any = []) => {
        this.lookups[item.option] = data;
        if (index === marketingCodeLookup.length - 1) {
          subj.next(this.lookups);
          subj.complete();
        }
      });
    });
    return subj.asObservable();
  }

  cancelClickHandler() {
    this.informationPageService.emitCancel(BRAND_NAME);
  }

  saveChangesClickHandler() {
    this.informationPageService.emitShowBeforeSaveModal();
  }

  refresh(): void {
    this.refreshSegmentData();
    this.showLoader();
    this.refreshData().subscribe((data: any) => {
      this.setData(data);
    });
  }

  protected showLoader() {
    this.formComponent.loader.show();
  }

  protected hideLoader() {
    this.formComponent.loader.hide();
  }
}
